import Buttons from '@ui/elements/Buttons'
import Div from '@ui/layout/Div'
import Icon from '@ui/elements/Icon'
import useToggle from '@hook/useToggle'
import { useDispatch, useSelector } from 'react-redux'
import { updateFilters } from '@store/slice/sterilizationSlice'
import { useTranslation } from 'react-i18next'
import { func, number } from 'prop-types'

const SterilizationListFilters = ({ getCalls, nbAddresses }) => {
  const [t] = useTranslation('common')
  const [isFilterActive, toggleFilter] = useToggle(false)
  const { filters, activeFilters } = useSelector(state => state.sterilization)
  const dispatch = useDispatch()

  const updateValue = (activeValues, valueToManage) => {
    const updatedActiveValues = [...activeValues]
    const propIndex = updatedActiveValues.indexOf(valueToManage)
    propIndex >= 0 ? updatedActiveValues.splice(propIndex, 1) : updatedActiveValues.push(valueToManage)
    return updatedActiveValues.length ? updatedActiveValues.join(',') : null
  }

  const { districts, nbNests, callTypes, scanPhotosNests, scanPhotosBirds, scanPhotosEggs } = filters || {}

  const activeDistricts = null !== activeFilters.districts ? activeFilters.districts.split(',') : []
  const activeNbNests = null !== activeFilters.nbNests ? activeFilters.nbNests.split(',') : []
  const activeCallTypes = null !== activeFilters.callTypes ? activeFilters.callTypes.split(',') : []
  const activeScanPhotosNests = null !== activeFilters.scanPhotosNests ? activeFilters.scanPhotosNests.split(',') : []
  const activeScanPhotosBirds = null !== activeFilters.scanPhotosBirds ? activeFilters.scanPhotosBirds.split(',') : []
  const activeScanPhotosEggs = null !== activeFilters.scanPhotosEggs ? activeFilters.scanPhotosEggs.split(',') : []

  const renderFilters = () => {
    const buttons = []
    if (null === filters || !Object.keys(filters).length) {
      return buttons
    }

    for (let prop in districts) {
      const district = districts[prop]
      const propIndex = activeDistricts.indexOf(prop)
      buttons.push({
        icon: 'icon-map',
        cssClass: `is-small ${propIndex >= 0 ? 'is-focused is-link' : 'is-info '}`,
        callback: () => { dispatch(updateFilters({ ...activeFilters, districts: updateValue(activeDistricts, prop) })) },
        text: `${district.name}(${district.nbCalls})`
      });
    }

    for (let prop in nbNests) {
      const propIndex = activeNbNests.indexOf(prop)
      buttons.push({
        icon: 'icon-swap_calls',
        cssClass: `is-small ${propIndex >= 0 ? 'is-focused is-link' : 'is-info '}`,
        callback: () => { dispatch(updateFilters({ ...activeFilters, nbNests: updateValue(activeNbNests, prop) })) },
        text: `${t('labels.nests_number', { nestsNumber: prop })}(${nbNests[prop]})`
      });
    }

    for (let prop in callTypes) {
      const propIndex = activeCallTypes.indexOf(prop)
      buttons.push({
        icon: 'citizen' === prop ? 'icon-supervisor_account' : 'icon-apartment',
        cssClass: `is-small ${propIndex >= 0 ? 'is-focused is-link' : 'is-info '}`,
        callback: () => { dispatch(updateFilters({ ...activeFilters, callTypes: updateValue(activeCallTypes, prop) })) },
        text: `${t(`labels.${prop}_calls`)}(${callTypes[prop]})`
      });
    }

    for (let prop in scanPhotosNests) {
      const propIndex = activeScanPhotosNests.indexOf(prop)
      buttons.push({
        icon: 'citizen' === prop ? 'icon-apartment' : 'icon-supervisor_account',
        cssClass: `is-small ${propIndex >= 0 ? 'is-focused is-link' : 'is-info '}`,
        callback: () => { dispatch(updateFilters({ ...activeFilters, scanPhotosNests: updateValue(activeScanPhotosNests, prop) })) },
        text: `${t(`labels.scan_found_nests`, { nestsNumber: prop ? prop : t(`labels.none`) })}(${scanPhotosNests[prop]})`
      });
    }

    for (let prop in scanPhotosBirds) {
      const propIndex = activeScanPhotosBirds.indexOf(prop)
      buttons.push({
        icon: 'citizen' === prop ? 'icon-apartment' : 'icon-supervisor_account',
        cssClass: `is-small ${propIndex >= 0 ? 'is-focused is-link' : 'is-info '}`,
        callback: () => { dispatch(updateFilters({ ...activeFilters, scanPhotosBirds: updateValue(activeScanPhotosBirds, prop) })) },
        text: `${t(`labels.scan_found_birds`, { birdsNumber: prop ? prop : t(`labels.none`) })}(${scanPhotosBirds[prop]})`
      });
    }

    for (let prop in scanPhotosEggs) {
      const propIndex = activeScanPhotosEggs.indexOf(prop)
      buttons.push({
        icon: 'citizen' === prop ? 'icon-apartment' : 'icon-supervisor_account',
        cssClass: `is-small ${propIndex >= 0 ? 'is-focused is-link' : 'is-info '}`,
        callback: () => { dispatch(updateFilters({ ...activeFilters, scanPhotosEggs: updateValue(activeScanPhotosEggs, prop) })) },
        text: `${t(`labels.scan_found_eggs`, { eggsNumber: prop ? prop : t(`labels.none`) })}(${scanPhotosEggs[prop]})`
      });
    }

    return buttons
  }

  const isFiltersOn = () => {
    let isOn = false
    for (let prop in activeFilters) {
      if (!isOn && null !== activeFilters[prop]) {
        isOn = true
      }
    }

    return isOn
  }

  const clearFilters = () => {
    dispatch(updateFilters({ districts: null, nbNests: null, callTypes: null, scanPhotosNests: null, scanPhotosBirds: null, scanPhotosEggs: null }))
    getCalls(true)
  }

  return (
    <Div cssClass={`filters-zone ${isFilterActive ? 'is-down' : 'is-up'}`}>
      <Div cssClass='filters-header'>
        <h2 onClick={() => toggleFilter()}>{t(`titles.filter`)} ({t('labels.nb_addresses', { nbAddresses: nbAddresses })})</h2>
        <Div cssClass='command-zone'>
          <Buttons buttons={[
            {
              icon: 'icon-filter_list_alt',
              cssClass: 'is-success is-small',
              callback: () => getCalls(),
              text: t('actions.filter')
            }, {
              icon: 'icon-clear',
              cssClass: `is-danger is-small ${!isFiltersOn() ? 'is-hidden' : ''}`,
              callback: () => clearFilters(),
              text: t('actions.clear')
            }
          ]} />
          <Icon
            spanClass='filter-arrow'
            icon={`icon-keyboard_arrow_${isFilterActive ? 'down' : 'up'}`}
            callback={() => toggleFilter()}
          />
        </Div>
      </Div>
      <Div cssClass={`filters ${isFilterActive ? 'is-active' : 'is-hidden'}`}>
        <Buttons buttons={renderFilters()} />
      </Div>
    </Div>
  )
}

SterilizationListFilters.propTypes = {
  getCalls: func,
  nbAddresses: number
}

export default SterilizationListFilters